import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { findAirportByCode, type Airport } from '@flymind/shared';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../lib/api-client';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { AirportSearch } from '../components/AirportSearch';

const TOTAL_STEPS = 3;

function ProgressBar({ step, onStepClick }: { step: number; onStepClick?: (s: number) => void }) {
  const { t } = useTranslation();
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`flex items-center gap-2 ${s < step ? 'cursor-pointer' : ''}`} onClick={() => s < step && onStepClick?.(s)}>
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-colors ${
                s < step
                  ? 'bg-primary-600 text-white hover:bg-primary-700 transition-colors'
                  : s === step
                    ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                    : 'bg-surface-secondary text-text-secondary'
              }`}
            >
              {s < step ? (
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                s
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-1">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full transition-colors ${
              s <= step ? 'bg-primary-600' : 'bg-surface-secondary'
            }`}
          />
        ))}
      </div>
      <p className="mt-2 text-center text-xs text-text-secondary">
        {t('onboarding.stepOf').replace('{step}', String(step)).replace('{total}', String(TOTAL_STEPS))}
      </p>
    </div>
  );
}

// Step 1: Airport selection
function StepAirports({
  departure,
  arrival,
  onDepartureChange,
  onArrivalChange,
  errors,
}: {
  departure: Airport | null;
  arrival: Airport | null;
  onDepartureChange: (a: Airport | null) => void;
  onArrivalChange: (a: Airport | null) => void;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-text-primary">
          {t('onboarding.chooseRoute')}
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          {t('onboarding.chooseRouteSubtitle')}
        </p>
      </div>

      <AirportSearch
        value={departure}
        onChange={onDepartureChange}
        label={t('monitor.departureAirport')}
        placeholder={t('monitor.searchAirport')}
        error={errors.aeroportoPartenza}
      />

      <AirportSearch
        value={arrival}
        onChange={onArrivalChange}
        label={t('monitor.arrivalAirport')}
        placeholder={t('monitor.searchAirport')}
        error={errors.aeroportoArrivo}
      />
    </div>
  );
}

// Step 2: Dates & Budget
function StepDatesAndBudget({
  monitoringType,
  departureDate,
  returnDate,
  oneWay,
  periodoInizio,
  periodoFine,
  budget,
  passengers,
  classeCabina,
  soggiornoMin,
  soggiornoMax,
  onMonitoringTypeChange,
  onDepartureDateChange,
  onReturnDateChange,
  onOneWayChange,
  onPeriodoInizioChange,
  onPeriodoFineChange,
  onBudgetChange,
  onPassengersChange,
  onClasseCabinaChange,
  onSoggiornoMinChange,
  onSoggiornoMaxChange,
  errors,
}: {
  monitoringType: 'fisso' | 'flessibile';
  departureDate: string;
  returnDate: string;
  oneWay: boolean;
  periodoInizio: string;
  periodoFine: string;
  budget: string;
  passengers: number;
  classeCabina: string | null;
  soggiornoMin: number | null;
  soggiornoMax: number | null;
  onMonitoringTypeChange: (v: 'fisso' | 'flessibile') => void;
  onDepartureDateChange: (v: string) => void;
  onReturnDateChange: (v: string) => void;
  onOneWayChange: (v: boolean) => void;
  onPeriodoInizioChange: (v: string) => void;
  onPeriodoFineChange: (v: string) => void;
  onBudgetChange: (v: string) => void;
  onPassengersChange: (v: number) => void;
  onClasseCabinaChange: (v: string | null) => void;
  onSoggiornoMinChange: (v: number | null) => void;
  onSoggiornoMaxChange: (v: number | null) => void;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().split('T')[0];

  // Max date for periodoFine: periodoInizio + 30 days
  const periodoFineMax = periodoInizio
    ? (() => {
        const d = new Date(periodoInizio + 'T00:00:00');
        d.setDate(d.getDate() + 30);
        return d.toISOString().split('T')[0];
      })()
    : undefined;

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-text-primary">
          {t('onboarding.datesAndBudget')}
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          {t('onboarding.datesAndBudgetSubtitle')}
        </p>
      </div>

      {/* Monitoring type toggle */}
      <div className="flex rounded-lg border border-border bg-surface-secondary p-1">
        <button
          type="button"
          onClick={() => onMonitoringTypeChange('fisso')}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            monitoringType === 'fisso'
              ? 'bg-surface text-text-primary shadow-sm'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {t('onboarding.fixedDates')}
        </button>
        <button
          type="button"
          onClick={() => onMonitoringTypeChange('flessibile')}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            monitoringType === 'flessibile'
              ? 'bg-surface text-text-primary shadow-sm'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {t('onboarding.flexiblePeriod')}
        </button>
      </div>

      {monitoringType === 'fisso' ? (
        <>
          <Input
            label={t('monitor.departureDate')}
            type="date"
            value={departureDate}
            onChange={(e) => onDepartureDateChange(e.target.value)}
            min={today}
            error={errors.dataAndata}
            required
          />

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={oneWay}
                onChange={(e) => onOneWayChange(e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-text-primary">
                {t('monitor.oneWay')}
              </span>
            </label>

            {!oneWay && (
              <Input
                label={t('monitor.returnDate')}
                type="date"
                value={returnDate}
                onChange={(e) => onReturnDateChange(e.target.value)}
                min={departureDate || today}
                error={errors.dataRitorno}
              />
            )}
          </div>
        </>
      ) : (
        <>
          <Input
            label={t('onboarding.periodStart')}
            type="date"
            value={periodoInizio}
            onChange={(e) => onPeriodoInizioChange(e.target.value)}
            min={today}
            error={errors.periodoInizio}
            required
          />

          <Input
            label={t('onboarding.periodEnd')}
            type="date"
            value={periodoFine}
            onChange={(e) => onPeriodoFineChange(e.target.value)}
            min={periodoInizio || today}
            max={periodoFineMax}
            error={errors.periodoFine}
            required
          />

          <div className="rounded-lg bg-primary-50 border border-primary-200 px-4 py-3">
            <p className="text-sm text-primary-800">
              {t('onboarding.flexibleHint')}
            </p>
          </div>

          <div className="mt-4">
            <label className="block text-sm text-text-secondary mb-2">{t('onboarding.stayDurationLabel')}</label>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs text-text-secondary mb-1">{t('onboarding.minDays')}</label>
                <input
                  type="number"
                  value={soggiornoMin ?? ''}
                  onChange={(e) => onSoggiornoMinChange(e.target.value ? Number(e.target.value) : null)}
                  placeholder={t('onboarding.daysExample3')}
                  min="1"
                  max="30"
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-text-secondary mb-1">{t('onboarding.maxDays')}</label>
                <input
                  type="number"
                  value={soggiornoMax ?? ''}
                  onChange={(e) => onSoggiornoMaxChange(e.target.value ? Number(e.target.value) : null)}
                  placeholder={t('onboarding.daysExample7')}
                  min="1"
                  max="30"
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            {errors.soggiornoMin && (
              <p className="text-xs text-alert-exceptional mt-1">{errors.soggiornoMin}</p>
            )}
          </div>
        </>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-text-primary">
          {t('monitor.budgetOptional')}
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary">
            &euro;
          </span>
          <input
            type="number"
            value={budget}
            onChange={(e) => onBudgetChange(e.target.value)}
            placeholder={t('onboarding.budgetExample')}
            min="1"
            className="w-full rounded-lg border border-border bg-surface pl-8 pr-3 py-2 text-sm text-text-primary placeholder:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        {errors.budgetMax && (
          <p className="text-xs text-alert-exceptional">{errors.budgetMax}</p>
        )}
      </div>

      {/* Flight preferences */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-text-primary mb-4">{t('onboarding.flightPreferences')}</h3>

        {/* Cabin class */}
        <div className="mb-4">
          <label className="block text-sm text-text-secondary mb-2">{t('onboarding.classLabel')}</label>
          <select
            value={classeCabina ?? ''}
            onChange={(e) => onClasseCabinaChange(e.target.value || null)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary"
          >
            <option value="">{t('onboarding.classEconomy')}</option>
            <option value="premium_economy">{t('onboarding.classPremiumEconomy')}</option>
            <option value="business">{t('onboarding.classBusiness')}</option>
            <option value="first">{t('onboarding.classFirst')}</option>
          </select>
        </div>

        {/* Passengers */}
        <div>
          <label className="block text-sm text-text-secondary mb-2">{t('onboarding.passengers')}</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onPassengersChange(Math.max(1, passengers - 1))}
              disabled={passengers <= 1}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-text-primary hover:bg-surface-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
              </svg>
            </button>
            <span className="min-w-[2rem] text-center text-lg font-semibold text-text-primary">
              {passengers}
            </span>
            <button
              type="button"
              onClick={() => onPassengersChange(Math.min(6, passengers + 1))}
              disabled={passengers >= 6}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-text-primary hover:bg-surface-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m-7-7h14" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 3: Confirmation
function StepConfirm({
  monitoringType,
  departure,
  arrival,
  departureDate,
  returnDate,
  oneWay,
  periodoInizio,
  periodoFine,
  budget,
  passengers,
  classeCabina,
  soggiornoMin,
  soggiornoMax,
}: {
  monitoringType: 'fisso' | 'flessibile';
  departure: Airport | null;
  arrival: Airport | null;
  departureDate: string;
  returnDate: string;
  oneWay: boolean;
  periodoInizio: string;
  periodoFine: string;
  budget: string;
  passengers: number;
  classeCabina: string | null;
  soggiornoMin: number | null;
  soggiornoMax: number | null;
}) {
  const { t, locale } = useTranslation();
  const dateLocale = locale === 'en' ? 'en-US' : 'it-IT';

  function formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(dateLocale, {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  function formatDateShort(dateStr: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(dateLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-text-primary">
          {t('onboarding.summary')}
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          {t('onboarding.summarySubtitle')}
        </p>
      </div>

      <Card variant="bordered" padding="md">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('monitor.departure')}</span>
            <span className="text-sm font-medium text-text-primary">
              {departure ? `${departure.city} (${departure.code})` : '-'}
            </span>
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('monitor.destination')}</span>
            <span className="text-sm font-medium text-text-primary">
              {arrival ? `${arrival.city} (${arrival.code})` : '-'}
            </span>
          </div>
          <div className="h-px bg-border" />

          {monitoringType === 'fisso' ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('monitor.departureDate')}</span>
                <span className="text-sm font-medium text-text-primary">
                  {formatDate(departureDate)}
                </span>
              </div>
              <div className="h-px bg-border" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">
                  {oneWay ? t('monitor.oneWay') : t('monitor.returnDate')}
                </span>
                <span className="text-sm font-medium text-text-primary">
                  {oneWay ? t('common.yes') : formatDate(returnDate)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('onboarding.type')}</span>
                <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">
                  {t('onboarding.flexibleMonitoring')}
                </span>
              </div>
              <div className="h-px bg-border" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('onboarding.period')}</span>
                <span className="text-sm font-medium text-text-primary">
                  {formatDateShort(periodoInizio)} - {formatDateShort(periodoFine)}
                </span>
              </div>
            </>
          )}

          {budget && (
            <>
              <div className="h-px bg-border" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('monitor.budget')}</span>
                <span className="text-sm font-medium text-text-primary">
                  &euro; {budget}
                </span>
              </div>
            </>
          )}
          <div className="h-px bg-border" />
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('onboarding.passengers')}</span>
            <span className="text-sm font-medium text-text-primary">
              {passengers}
            </span>
          </div>

          {classeCabina && (
            <>
              <div className="h-px bg-border" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('onboarding.classLabel')}</span>
                <span className="text-sm font-medium text-text-primary">
                  {{
                    premium_economy: t('onboarding.classPremiumEconomy'),
                    business: t('onboarding.classBusiness'),
                    first: t('onboarding.classFirst'),
                  }[classeCabina]}
                </span>
              </div>
            </>
          )}

          {(soggiornoMin !== null || soggiornoMax !== null) && (
            <>
              <div className="h-px bg-border" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('onboarding.stay')}</span>
                <span className="text-sm font-medium text-text-primary">
                  {soggiornoMin != null && soggiornoMax != null
                    ? `${soggiornoMin}\u2013${soggiornoMax} ${t('onboarding.days')}`
                    : soggiornoMin != null
                      ? `${t('onboarding.minPrefix')} ${soggiornoMin} ${t('onboarding.days')}`
                      : `${t('onboarding.maxPrefix')} ${soggiornoMax} ${t('onboarding.days')}`}
                </span>
              </div>
            </>
          )}
        </div>
      </Card>

      <div className="rounded-lg bg-primary-50 border border-primary-200 px-4 py-3">
        <p className="text-sm text-primary-800">
          {t('onboarding.aiAdvice')}
        </p>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const submitClickedRef = useRef(false);

  // Step 1 state
  const [departure, setDeparture] = useState<Airport | null>(null);
  const [arrival, setArrival] = useState<Airport | null>(null);

  // Step 2 state
  const [monitoringType, setMonitoringType] = useState<'fisso' | 'flessibile'>('fisso');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [oneWay, setOneWay] = useState(false);
  const [periodoInizio, setPeriodoInizio] = useState('');
  const [periodoFine, setPeriodoFine] = useState('');
  const [soggiornoMin, setSoggiornoMin] = useState<number | null>(null);
  const [soggiornoMax, setSoggiornoMax] = useState<number | null>(null);
  const [budget, setBudget] = useState('');
  const [passengers, setPassengers] = useState(1);

  // Flight preferences (Step 2)
  const [classeCabina, setClasseCabina] = useState<string | null>(null); // null = economy default

  // Read voice search params from URL (?voice=<base64-json>)
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const voiceData = searchParams.get('voice');
    if (!voiceData) return;

    try {
      const fields = JSON.parse(atob(voiceData));

      // Pre-fill airports by IATA code
      if (fields.aeroportoPartenza) {
        const found = findAirportByCode(fields.aeroportoPartenza);
        if (found) setDeparture(found);
      }
      if (fields.aeroportoArrivo) {
        const found = findAirportByCode(fields.aeroportoArrivo);
        if (found) setArrival(found);
      }

      // Pre-fill monitoring type
      if (fields.tipoMonitoraggio) setMonitoringType(fields.tipoMonitoraggio);

      // Pre-fill dates
      if (fields.dataAndata) setDepartureDate(fields.dataAndata);
      if (fields.dataRitorno) setReturnDate(fields.dataRitorno);
      if (fields.soloAndata !== undefined) setOneWay(fields.soloAndata);
      if (fields.periodoInizio) setPeriodoInizio(fields.periodoInizio);
      if (fields.periodoFine) setPeriodoFine(fields.periodoFine);

      // Pre-fill budget (stored as string in the form)
      if (fields.budgetMax != null) setBudget(String(fields.budgetMax));

      // Pre-fill passengers
      if (fields.passeggeri) setPassengers(fields.passeggeri);

      // Pre-fill flight preferences
      if (fields.classeCabina) setClasseCabina(fields.classeCabina);
      if (fields.soggiornoMin != null) setSoggiornoMin(fields.soggiornoMin);
      if (fields.soggiornoMax != null) setSoggiornoMax(fields.soggiornoMax);
    } catch {
      // Invalid voice data - ignore silently
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function validateStep1(): boolean {
    const errs: Record<string, string> = {};
    if (!departure) {
      errs.aeroportoPartenza = t('onboarding.errSelectDeparture');
    }
    if (!arrival) {
      errs.aeroportoArrivo = t('onboarding.errSelectArrival');
    }
    if (departure && arrival && departure.code === arrival.code) {
      errs.aeroportoArrivo = t('errors.sameAirport');
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function validateStep2(): boolean {
    const errs: Record<string, string> = {};

    if (monitoringType === 'fisso') {
      if (!departureDate) {
        errs.dataAndata = t('onboarding.errSelectDepartureDate');
      } else {
        const depDate = new Date(departureDate + 'T00:00:00');
        if (depDate <= new Date()) {
          errs.dataAndata = t('errors.pastDate');
        }
      }
      if (!oneWay && returnDate) {
        const depDate = new Date(departureDate + 'T00:00:00');
        const retDate = new Date(returnDate + 'T00:00:00');
        if (retDate <= depDate) {
          errs.dataRitorno = t('errors.returnBeforeDeparture');
        }
      }
    } else {
      if (!periodoInizio) {
        errs.periodoInizio = t('onboarding.errSelectStartDate');
      }
      if (!periodoFine) {
        errs.periodoFine = t('onboarding.errSelectEndDate');
      }
      if (periodoInizio && periodoFine) {
        const inizio = new Date(periodoInizio + 'T00:00:00');
        const fine = new Date(periodoFine + 'T00:00:00');
        if (fine <= inizio) {
          errs.periodoFine = t('onboarding.errEndAfterStart');
        } else {
          const diffDays = Math.ceil(
            (fine.getTime() - inizio.getTime()) / (1000 * 60 * 60 * 24)
          );
          if (diffDays > 30) {
            errs.periodoFine = t('onboarding.errMaxPeriod');
          }
        }
      }
      if (soggiornoMin != null && soggiornoMax != null && soggiornoMin > soggiornoMax) {
        errs.soggiornoMin = t('onboarding.errStayMinMax');
      }
    }

    if (budget && (isNaN(Number(budget)) || Number(budget) <= 0)) {
      errs.budgetMax = t('onboarding.errBudgetPositive');
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext() {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  }

  function handleBack() {
    setErrors({});
    setApiError('');
    if (step > 1) setStep(step - 1);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (step !== TOTAL_STEPS || !submitClickedRef.current) {
      return;
    }
    submitClickedRef.current = false;
    setApiError('');
    setIsLoading(true);

    try {
      const body: Record<string, unknown> = {
        aeroportoPartenza: departure!.code,
        aeroportoArrivo: arrival!.code,
        tipoMonitoraggio: monitoringType,
        budgetMax: budget ? Number(budget) : null,
        classeCabina: classeCabina,
        passeggeri: passengers,
      };

      if (monitoringType === 'fisso') {
        body.dataAndata = departureDate;
        body.dataRitorno = oneWay ? null : returnDate || null;
        body.soloAndata = oneWay;
      } else {
        body.periodoInizio = periodoInizio;
        body.periodoFine = periodoFine;
        body.soggiornoMin = soggiornoMin;
        body.soggiornoMax = soggiornoMax;
      }

      await api.post('/monitoraggi', body);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('errors.generic');
      setApiError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-surface to-primary-100 p-4">
      <div className="w-full max-w-lg">
        {/* Branding */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600 shadow-lg">
            <span className="text-xl font-bold text-white">FM</span>
          </div>
        </div>

        <Card variant="elevated" padding="lg">
          <ProgressBar step={step} onStepClick={(s) => { setErrors({}); setApiError(''); setStep(s); }} />

          {step === 1 && (
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1 text-sm text-text-secondary hover:text-primary-600 transition-colors mb-4 cursor-pointer"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              {t('onboarding.backToDashboard')}
            </button>
          )}

          {apiError && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {step === 1 && (
              <StepAirports
                departure={departure}
                arrival={arrival}
                onDepartureChange={setDeparture}
                onArrivalChange={setArrival}
                errors={errors}
              />
            )}

            {step === 2 && (
              <StepDatesAndBudget
                monitoringType={monitoringType}
                departureDate={departureDate}
                returnDate={returnDate}
                oneWay={oneWay}
                periodoInizio={periodoInizio}
                periodoFine={periodoFine}
                budget={budget}
                passengers={passengers}
                classeCabina={classeCabina}
                onMonitoringTypeChange={setMonitoringType}
                onDepartureDateChange={setDepartureDate}
                onReturnDateChange={setReturnDate}
                onOneWayChange={setOneWay}
                onPeriodoInizioChange={setPeriodoInizio}
                onPeriodoFineChange={setPeriodoFine}
                onBudgetChange={setBudget}
                onPassengersChange={setPassengers}
                onClasseCabinaChange={setClasseCabina}
                soggiornoMin={soggiornoMin}
                soggiornoMax={soggiornoMax}
                onSoggiornoMinChange={setSoggiornoMin}
                onSoggiornoMaxChange={setSoggiornoMax}
                errors={errors}
              />
            )}

            {step === 3 && (
              <StepConfirm
                monitoringType={monitoringType}
                departure={departure}
                arrival={arrival}
                departureDate={departureDate}
                returnDate={returnDate}
                oneWay={oneWay}
                periodoInizio={periodoInizio}
                periodoFine={periodoFine}
                budget={budget}
                passengers={passengers}
                classeCabina={classeCabina}
                soggiornoMin={soggiornoMin}
                soggiornoMax={soggiornoMax}
              />
            )}

            <div className="mt-8 flex gap-3">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={handleBack}
                  className="flex-1"
                >
                  {t('common.back')}
                </Button>
              )}

              {step < TOTAL_STEPS ? (
                <Button
                  type="button"
                  size="lg"
                  onClick={handleNext}
                  className="flex-1"
                >
                  {t('common.next')}
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="lg"
                  isLoading={isLoading}
                  className="flex-1"
                  onClick={() => { submitClickedRef.current = true; }}
                >
                  {t('monitor.create')}
                </Button>
              )}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
